import { CHRISTMAS_CONFIG, CHRISTMAS_MESSAGES, DELIVERY, FULFILLMENT_METHODS } from '../constants';
import { getExcludedStateSth } from '../../pod/helpers/pod-fulfillment-utils';

const getFastestDate = (datesList = []) => (
  datesList.length > 0 ? new Date(Math.min(...datesList)) : new Date()
);

const createValidDate = (dateString, skipDateCreation = false) => {
  if (!dateString) return null;

  const date = new Date(dateString);
  if (!Number.isNaN(date.getTime())) {
    return skipDateCreation ? date : new Date(dateString?.replace(/-/g, '/'));
  }
  return null;
};

export const isPastMonth = (referenceDate = null) => {
  if (!referenceDate) return false;
  const currentMonth = new Date()?.getMonth() || 0;
  const targetMonth = new Date(referenceDate || '')?.getMonth() || 0;
  return targetMonth < currentMonth;
};

export const getApplianceDeliveryDate = (applianceDeliveryDataETA) => {
  if (!applianceDeliveryDataETA?.earliestAvailabilityDate) return null;
  let applianceDeliveryData = new Date(applianceDeliveryDataETA?.earliestAvailabilityDate);
  // eslint-disable-next-line no-unused-expressions
  applianceDeliveryData?.setFullYear(new Date().getFullYear());

  // If past month, set to next year
  if (isPastMonth(applianceDeliveryData)) {
    // eslint-disable-next-line no-unused-expressions
    applianceDeliveryData?.setFullYear(new Date().getFullYear() + 1);
    return createValidDate(applianceDeliveryData, true);
  }
  return createValidDate(applianceDeliveryData, true);
};

const processPriorityDeliveryOptions = (priorityDeliveryOptions) => {
  if (!priorityDeliveryOptions || priorityDeliveryOptions.length === 0) return null;

  const validDates = priorityDeliveryOptions?.map((option) => createValidDate(option?.date)).filter(Boolean);
  return validDates.length > 0 ? getFastestDate(validDates) : null;
};

const getServiceDeliveryDate = (service) => {
  if (!service?.deliveryDates && !service?.earliestDeliveryDate) return null;

  // searchModel returns earliestDeliveryDate for major Appliance
  const dateString = service?.deliveryDates?.endDate || service?.deliveryDates?.startDate
   || service?.earliestDeliveryDate;
  return createValidDate(dateString);
};

const processRegularDeliveryServices = (deliveryService, fulfillment) => {
  const bodfsFulfillment = (deliveryService.services || []).find(
    (service) => service.type === FULFILLMENT_METHODS.EXPRESS_DELIVERY
  );
  const sthFulfillment = (deliveryService.services || []).find(
    (service) => service.type === FULFILLMENT_METHODS.STH_FFM
  );

  const validDates = [
    getServiceDeliveryDate(bodfsFulfillment),
    !getExcludedStateSth({ fulfillment }) && getServiceDeliveryDate(sthFulfillment)
  ].filter(Boolean);

  return validDates.length > 0 ? getFastestDate(validDates) : null;
};

const getFulfillmentDeliveryDate = (fulfillment) => {
  if (!fulfillment) return null;

  const deliveryService = (fulfillment?.fulfillmentOptions || []).find(
    (option) => option.type === DELIVERY
  );

  if (!deliveryService) return null;

  // Try priority delivery options first
  const priorityDate = processPriorityDeliveryOptions(deliveryService?.priorityDeliveryOptions);
  if (priorityDate) return priorityDate;

  // Fall back to regular delivery services
  return processRegularDeliveryServices(deliveryService, fulfillment);
};

const isFutureDate = (deliveryDate, referenceDate) => {
  if (!deliveryDate || !referenceDate) return false;
  const targetDate = new Date(deliveryDate);
  return targetDate.getTime() > referenceDate.getTime();
};

const isDeliveryDateDec24 = (deliveryETADate) => {
  if (!deliveryETADate) return false;
  const today = new Date();
  const deliveryDate = new Date(today.getFullYear(), 11, 24); // December 24th
  return deliveryETADate.getTime() === deliveryDate.getTime();
};

const determineChristmasMessage = (deliveryDate, bufferDays, checkArrivesAfter = true) => {
  const bufferDaysNum = parseInt(bufferDays, 10);
  let christmasArrivesByEligibleDate = CHRISTMAS_CONFIG.DAY - bufferDaysNum;
  christmasArrivesByEligibleDate = new Date(new Date().getFullYear(), 11, christmasArrivesByEligibleDate);

  const isFutureDeliveryDate = isFutureDate(deliveryDate, christmasArrivesByEligibleDate);
  // Check if arrives by Christmas
  if (!isFutureDeliveryDate) {
    return CHRISTMAS_MESSAGES.BY_CHRISTMAS;
  }
  // Display Arrives After message only till 25th midnight
  if (checkArrivesAfter && isFutureDeliveryDate) {
    return CHRISTMAS_MESSAGES.AFTER_CHRISTMAS;
  }
  return null;
};

const isTodayChristmasOrBefore = () => {
  const today = new Date();
  const afterChristmasDay = new Date(today.getFullYear(), 11, 26); // December 26th
  return today < afterChristmasDay;
};

const isEligibleForChristmasMessaging = (deliveryDate) => {
  if (!deliveryDate) return false;
  if (isDeliveryDateDec24(deliveryDate)) return false;
  // Additional validation: ensure it's a reasonable Christmas delivery timeframe
  const currentYear = new Date().getFullYear();
  const deliveryYear = deliveryDate.getFullYear();

  // Only consider ongoing year for Christmas messaging
  return deliveryYear >= currentYear;
};

export const getChristmasDeliveryMessage = ({
  applianceDeliveryData,
  fulfillment = null,
  bufferDays = 0,
  checkArrivesAfter = true
}) => {
  try {
    // Early return if no data sources and no buffer days
    // after christmas do not return any messages
    if (parseInt(bufferDays, 10) === 0 || !isTodayChristmasOrBefore() || ((!applianceDeliveryData && !fulfillment))) {
      return null;
    }

    // Try to get delivery date from appliance data first, then fulfillment data
    const deliveryETA = getApplianceDeliveryDate(applianceDeliveryData)
      || getFulfillmentDeliveryDate(fulfillment);

    // Validate if the delivery date is eligible for Christmas messaging
    if (!isEligibleForChristmasMessaging(deliveryETA)) {
      return null;
    }

    return !deliveryETA ? null : determineChristmasMessage(deliveryETA, bufferDays, checkArrivesAfter);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error in getChristmasDeliveryMessage:', error);
    return null;
  }
};